import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, SpinnerGap, Warning } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SubscriptionSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithToken } = useAuth();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('verifying'); // verifying | activating | success | error
  const [message, setMessage] = useState('Verifying your payment...');
  const polled = useRef(false);

  useEffect(() => {
    if (!sessionId || polled.current) return;
    polled.current = true;

    const verify = async () => {
      let attempts = 0;
      const maxAttempts = 12;

      while (attempts < maxAttempts) {
        attempts++;
        try {
          const res = await axios.get(`${API_URL}/api/companies/subscription/status/${sessionId}`);
          if (res.data.payment_status === 'paid') {
            setStatus('activating');
            setMessage('Payment confirmed! Setting up your account...');

            // Auto-login
            try {
              const loginRes = await axios.post(`${API_URL}/api/companies/auto-login`, { session_id: sessionId });
              const { access_token, user } = loginRes.data;

              // Store token and user via AuthContext
              loginWithToken(access_token, user);

              setStatus('success');
              setMessage('Welcome aboard! Launching your dashboard...');

              // Redirect to dashboard with setup flag
              setTimeout(() => {
                navigate('/dashboard?setup=true', { replace: true });
              }, 1500);
              return;
            } catch (loginErr) {
              // Payment worked but auto-login failed - send to manual login
              setStatus('success');
              setMessage('Account activated! Redirecting to login...');
              setTimeout(() => navigate('/login?activated=true', { replace: true }), 2000);
              return;
            }
          }
        } catch {
          // Retry
        }

        // Wait before next poll
        await new Promise(r => setTimeout(r, 2500));
        setMessage(`Verifying your payment... (${attempts}/${maxAttempts})`);
      }

      setStatus('error');
      setMessage('Payment verification is taking longer than expected. Please log in manually — your account should be active shortly.');
    };

    verify();
  }, [sessionId, navigate, loginWithToken]);

  return (
    <div className="min-h-screen bg-[#0B1120] flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full bg-white/[0.03] border border-white/10 rounded-2xl p-8 text-center"
        data-testid="subscription-success-page"
      >
        {status === 'verifying' && (
          <>
            <SpinnerGap size={48} className="text-[#FF9900] mx-auto mb-4 animate-spin" />
            <h1 className="text-2xl font-bold text-white mb-2">Processing Payment</h1>
          </>
        )}

        {status === 'activating' && (
          <>
            <SpinnerGap size={48} className="text-[#FF9900] mx-auto mb-4 animate-spin" />
            <h1 className="text-2xl font-bold text-white mb-2">Activating Account</h1>
          </>
        )}

        {status === 'success' && (
          <>
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring' }}>
              <CheckCircle size={56} weight="fill" className="text-emerald-400 mx-auto mb-4" />
            </motion.div>
            <h1 className="text-2xl font-bold text-white mb-2">You're All Set!</h1>
          </>
        )}

        {status === 'error' && (
          <>
            <Warning size={48} className="text-amber-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Almost There</h1>
          </>
        )}

        <p className="text-gray-400 text-sm mb-6">{message}</p>

        {status === 'error' && (
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-2.5 bg-[#FF9900] hover:bg-[#FF9900]/90 text-white font-bold text-sm rounded-lg transition-colors"
            data-testid="success-login-btn"
          >
            Go to Login
          </button>
        )}
      </motion.div>
    </div>
  );
}
